import { useState, useEffect } from "react";
import axios from "axios";
import { Percent, Save, Loader2, Info, TrendingUp } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CommissionManager() {
  const [settings, setSettings] = useState({
    commission_percent: 5.0,
    commission_description: "Prowizja portalu od wartosci zamowienia"
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/admin/commission-settings`);
      setSettings(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/commission-settings`, settings);
      toast.success("Ustawienia prowizji zapisane");
    } catch (err) {
      toast.error("Blad zapisu ustawien");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Przykladowe kalkulacje
  const examplePrices = [50, 100, 200, 500];

  return (
    <div className="space-y-6">
      <Card className="rounded-xl border shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Percent className="w-5 h-5 text-blue-500" />
            Ustawienia prowizji portalu
          </CardTitle>
          <CardDescription>
            Ustaw procent prowizji pobieranej od kazdego zamowienia. Prowizja jest doliczana do ceny uslugi i widoczna dla reklamodawcy.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Slider prowizji */}
          <div className="space-y-4">
            <Label className="text-sm font-semibold">Wysokosc prowizji</Label>
            <div className="flex items-center gap-6">
              <div className="flex-1">
                <Slider
                  data-testid="commission-slider"
                  value={[settings.commission_percent]}
                  onValueChange={(value) => setSettings({ ...settings, commission_percent: value[0] })}
                  min={0}
                  max={30}
                  step={0.5}
                  className="w-full"
                />
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Input
                  data-testid="commission-input"
                  type="number"
                  value={settings.commission_percent}
                  onChange={(e) => setSettings({ ...settings, commission_percent: parseFloat(e.target.value) || 0 })}
                  className="w-20 h-10 text-center text-lg font-bold"
                  min={0}
                  max={100}
                  step={0.5}
                />
                <span className="text-lg font-bold text-muted-foreground">%</span>
              </div>
            </div>
          </div>

          {/* Opis prowizji */}
          <div className="space-y-2">
            <Label className="text-sm font-semibold">Opis prowizji (widoczny wewnetrznie)</Label>
            <Input
              data-testid="commission-description"
              value={settings.commission_description}
              onChange={(e) => setSettings({ ...settings, commission_description: e.target.value })}
              placeholder="Np. Prowizja portalu od wartosci zamowienia"
            />
          </div>

          {/* Przykladowe kalkulacje */}
          <div className="p-4 rounded-lg bg-slate-50 dark:bg-slate-800/50 border">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="w-4 h-4 text-blue-500" />
              <p className="text-sm font-semibold">Przykladowe kalkulacje</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {examplePrices.map((price) => {
                const commission = (price * settings.commission_percent / 100).toFixed(2);
                const total = (price + parseFloat(commission)).toFixed(2);
                return (
                  <div key={price} className="p-3 rounded-lg bg-white dark:bg-slate-900 border text-center">
                    <p className="text-xs text-muted-foreground">Cena uslugi</p>
                    <p className="text-lg font-bold">{price} PLN</p>
                    <div className="my-2 border-t" />
                    <p className="text-xs text-muted-foreground">Prowizja ({settings.commission_percent}%)</p>
                    <p className="text-sm font-semibold text-blue-600">+{commission} PLN</p>
                    <div className="my-2 border-t" />
                    <p className="text-xs text-muted-foreground">Reklamodawca placi</p>
                    <p className="text-lg font-bold text-emerald-600">{total} PLN</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Info box */}
          <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
                <p className="font-semibold">Jak dziala prowizja?</p>
                <ul className="list-disc list-inside space-y-0.5 text-blue-700 dark:text-blue-300">
                  <li>Prowizja jest naliczana od ceny uslugi (bez dodatkowych kosztow)</li>
                  <li>Reklamodawca widzi rozbicie: cena uslugi + prowizja portalu</li>
                  <li>Wydawca otrzymuje pelna cene uslugi (bez prowizji)</li>
                  <li>Prowizja jest zapisywana w kazdym zamowieniu</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Przycisk zapisu */}
          <Button 
            data-testid="save-commission-btn"
            onClick={handleSave} 
            disabled={saving}
            className="w-full h-12 gap-2 text-base"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Zapisywanie...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Zapisz ustawienia prowizji
              </>
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
